import React, { useState, useEffect, useMemo } from "react";
// outsource
import { Row, Col, Button, Spin, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
// components
import PayoutSettingAfterHoursInfoBox from "./component/payout_setting_after_hours_info_box";
import PayoutSettingAfterHoursTable from "./component/payout_setting_after_hours_table";
// redux
import { useDispatch, useSelector } from "react-redux";
import {
  snapAfterHours,
  unSnapAfterHours,
  saveAfterHours,
} from "../../../store/payout_setting/payout_setting.action";
import {
  afterHoursSelector,
  afterHoursLoadingSelector,
} from "../../../store/payout_setting/payout_setting.selector";
import { branchsSelector } from "../../../store/branch/branch.selector";
import { snapBranchs, unSnapBranchs } from "../../../store/branch/branch.action";
import { calculatePayoutDailyData } from "../../../store/payout_daily/payout_daily.action";

const DEFAULT_TIME = { weekdays: "18:00", weekends: "17:00" };

const PayoutSettingAfterHoursTab = () => {
  // -------------------------- STATE --------------------------
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [recalculating, setRecalculating] = useState(false);

  // -------------------------- REDUX --------------------------
  const dispatch = useDispatch();
  const branches = useSelector(branchsSelector);
  const afterHoursSettings = useSelector(afterHoursSelector);
  const loading = useSelector(afterHoursLoadingSelector);

  // -------------------------- EFFECT --------------------------
  useEffect(() => {
    dispatch(snapBranchs());
    return () => dispatch(unSnapBranchs());
  }, [dispatch]);

  useEffect(() => {
    dispatch(snapAfterHours());
    return () => dispatch(unSnapAfterHours());
  }, [dispatch]);

  // -------------------------- FUNCTION --------------------------
  const mergedData = useMemo(() => {
    return (branches || []).map((b) => ({
      id: b.id,
      branch: `${b.name} | ${b.shopId}`,
      weekdays: afterHoursSettings?.[b.id]?.weekdays || DEFAULT_TIME.weekdays,
      weekends: afterHoursSettings?.[b.id]?.weekends || DEFAULT_TIME.weekends,
    }));
  }, [branches, afterHoursSettings]);

  const handleEdit = () => {
    setDraft(mergedData.map((r) => ({ ...r })));
    setEditing(true);
  };

  const handleCancel = () => setEditing(false);

  const handleSave = () => {
    const settingsMap = {};
    draft.forEach((r) => {
      settingsMap[r.id] = { weekdays: r.weekdays, weekends: r.weekends };
    });
    dispatch(saveAfterHours({ branches: settingsMap }));
    setEditing(false);
  };

  const handleRecalculate = () => {
    setRecalculating(true);
    dispatch(
      calculatePayoutDailyData((response) => {
        setRecalculating(false);
        if (response?.status === 200) {
          message.success("Recalculate successfully");
          return;
        }
        message.error(response?.data || "Recalculate failed");
      }),
    );
  };

  const handleChange = (id, field, value) => {
    setDraft((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  // -------------------------- RENDER --------------------------
  const renderActions = () => {
    return (
      <Row justify="end" gutter={[8, 0]} style={{ marginBottom: 12 }}>
        {!editing && (
          <Col>
            <Button
              className="ps-edit-btn ps-ah-action-btn ps-ah-recalculate-btn"
              icon={<SyncOutlined spin={recalculating} />}
              disabled={recalculating}
              onClick={handleRecalculate}
            >
              Recalculate
            </Button>
          </Col>
        )}
        {editing ? (
          <>
            <Col>
              <Button className="ps-cancel-btn" onClick={handleCancel}>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                className="ps-edit-btn"
                onClick={handleSave}
              >
                Save
              </Button>
            </Col>
          </>
        ) : (
          <Col>
            <Button type="primary" className="ps-edit-btn ps-ah-action-btn" onClick={handleEdit}>
              Edit
            </Button>
          </Col>
        )}
      </Row>
    );
  };

  const renderBody = () => {
    return (
      <Spin spinning={loading}>
        <Row gutter={[0, 12]}>
          <Col xl={24} lg={24} md={24} xs={24}>
            <PayoutSettingAfterHoursInfoBox />
          </Col>
          <Col xl={24} lg={24} md={24} xs={24}>
            {renderActions()}
          </Col>
          <Col xl={24} lg={24} md={24} xs={24}>
            <PayoutSettingAfterHoursTable
              rows={editing ? draft : mergedData}
              editing={editing}
              onChange={handleChange}
            />
          </Col>
        </Row>
      </Spin>
    );
  };

  // -------------------------- MAIN --------------------------
  return <div className="ps-section">{renderBody()}</div>;
};

export default PayoutSettingAfterHoursTab;
